import { BlockTitle } from "@blocks/Blocks";
import KeywordFilter from "@features/mybt/components/KeywordFilter";
import { useWindowSize } from "@hooks/useWindowSize";
import ArticleParagraph from "@routes/Article/components/ArticleParagraphElements/storyElement/ArticleParagraph";
import { FeedArticleItems, PodcastsTagType } from "@routes/Article/utils/types";
import { FeedTag } from "@routes/Keyword/utils/types";
import { TagType } from "@routes/Section/types";
import { getFormattedUrlPath } from "@util/helpers";

import PodcastComingUpNextComponent from "./PodcastComingUpNextComponent";

type PodcastAboutComponentProps = {
  /** Data for the podcast about section */
  podcastAboutData: PodcastsTagType;
  /** Data for the coming up next section */
  podcastsComingUpNextData: FeedArticleItems[];
  /** Current article title */
  title: string;
};

/**
 * Component for Podcast Article About Section.
 * https://www.figma.com/design/xsbEGttNPBTNUi388qtSJD/PODCASTS--WEB-?node-id=4844-133449&m=dev
 */
const PodcastAboutComponent = ({
  podcastAboutData,
  podcastsComingUpNextData,
  title,
}: PodcastAboutComponentProps): React.ReactElement => {
  const { isScreenLG } = useWindowSize();

  const { name, blurb, imageUrl, urlPath, id } = podcastAboutData;
  const tag: FeedTag = {
    name,
    urlPath,
    id,
    type: TagType.Keyword,
  };

  const tagUrlPath = getFormattedUrlPath(urlPath, [
    { key: "ref", value: "podcast-article-bottom" },
  ]);

  return (
    <div
      data-testid="podcast-about-component"
      className="border-t border-gray-850 pt-3"
    >
      <BlockTitle
        text="ABOUT THE PODCAST"
        rootClassName="mb-6 tracking-5% font-semibold font-poppins text-gray-850 text-base lg:text-lg"
      />

      <div className="mb-6 flex flex-row gap-6 lg:w-7/10">
        <a href={tagUrlPath.toString()} className="block flex-shrink-0">
          <img
            src={imageUrl}
            alt={name}
            width={isScreenLG ? 128 : 80}
            height={isScreenLG ? 128 : 80}
          />
        </a>

        <div className="flex flex-col gap-3 font-poppins">
          {podcastAboutData ? (
            <KeywordFilter
              tag={tag}
              className="inline-block w-fit bg-gray-250 py-2 pl-5 pr-3"
              tagClassName="flex-none border-r border-gray-350 pr-4"
              buttonClassName="ml-3 border-0"
              isPodcastAudioArticle
            />
          ) : null}

          {isScreenLG ? (
            <ArticleParagraph
              bodyString={blurb}
              className="font-poppins text-sm leading-6 text-gray-650"
            />
          ) : null}
        </div>
      </div>

      {!isScreenLG ? (
        <ArticleParagraph
          bodyString={blurb}
          className="font-poppins text-sm leading-6 text-gray-650"
        />
      ) : null}

      <PodcastComingUpNextComponent
        podcastsComingUpNextData={podcastsComingUpNextData}
        className="!mb-6"
        title={title}
        isFromAbout
      />
    </div>
  );
};

export default PodcastAboutComponent;
